import { ENTRANCE_TIME_SLOT_MODE_LIMITED } from '@components/advanced-settings-section';
import { SALES_START_TYPE_AFTER_OFFER } from '@components/basic-info-section';
import {
  CHANNEL_QUOTA_ALL,
  CHANNEL_QUOTA_UNLIMITED,
  type DistributionChannelLimitValue,
} from '@components/distribution-section';
import { formatItemsPerTransactionToBackend } from '@components/forms/utils/format-items-per-transaction-to-backend';
import { formatPriceToBackend } from '@components/forms/utils/format-price-to-backend';
import { formatSalesTimeToBackend } from '@components/forms/utils/format-sales-time-to-backend';
import { formatSalesStatusToBackend } from '@components/forms/utils/format-status-to-backend';
import { formatValidTimeToBackend } from '@components/forms/utils/format-valid-times-to-backend';
import { SIAE_STATUS_APPROVED, SIAE_STATUS_EXCLUDED } from '@components/regulation-settings-section';
import { type SupportedLanguages } from '@constants/constants.type';
import { type Admission, ADMISSION_STATUS_HIDDEN, type Translations } from '@domain/admission';
import { type FormFieldPreset } from '@domain/form-field-presets';
import { type Pass } from '@domain/passes';
import { type Channel, type CreateAdmissionRequest } from '@hooks/admissions/type';
import { calculateDirittoPrevendita } from '@utils/calculateDirittoPrevendita/calculateDirittoPrevendita';
import { collectChannelCapById, collectChannelIds, collectClosedChannelIds } from '@utils/channel-tree';
import { clearEmptyValues } from '@utils/clear-empty-fields';

import { type AdmissionFormType, type AdmissionFormValue } from '../admission-form.type';
import { valuesEqual } from '../validation';

import { mapHolderGroupToBackend } from './form-fields';

export interface ToBackendContext {
  value: AdmissionFormValue;
  type: Exclude<AdmissionFormType, 'pass'>;
  currency: string;
  mainLanguage: SupportedLanguages;
  eventId: string;
  eventStartingTime: number;
  eventEndingTime: number;
  timezone: string;
  offer?: Admission | null;
  offerPresetId?: string;
  defaultTicketsLeftThreshold: number;
  formFieldPresets: FormFieldPreset[];
  selectedOfferTriggerId: string | null;
}

export const toChannelSettings = (
  channelLimit: AdmissionFormValue['distribution']['channelLimit']
): { channels: Channel[]; isForAllChannels: boolean; channelDefaultMaxQuantity: number | null } => {
  if (!channelLimit?.enabled) {
    return { channels: [], isForAllChannels: true, channelDefaultMaxQuantity: null };
  }

  const { quotaMode, limitAllChannels, selectedChannelIds, channels: tree } = channelLimit;
  const selected = new Set(selectedChannelIds);
  const closed = collectClosedChannelIds(tree);
  const selectableIds = collectChannelIds(tree).filter((id) => !closed.has(id));
  const offeredIds = selectableIds.filter((id) => selected.has(id));
  const areAllSelected = selectableIds.every((id) => selected.has(id));

  if (quotaMode === CHANNEL_QUOTA_UNLIMITED) {
    return areAllSelected
      ? { channels: [], isForAllChannels: true, channelDefaultMaxQuantity: null }
      : {
          channels: offeredIds.map((id) => ({ id, maxQuantity: null })),
          isForAllChannels: false,
          channelDefaultMaxQuantity: null,
        };
  }

  if (quotaMode === CHANNEL_QUOTA_ALL) {
    const cap = limitAllChannels === '' ? null : Number(limitAllChannels);
    return areAllSelected
      ? { channels: [], isForAllChannels: true, channelDefaultMaxQuantity: cap }
      : {
          channels: offeredIds.map((id) => ({ id, maxQuantity: cap })),
          isForAllChannels: false,
          channelDefaultMaxQuantity: null,
        };
  }

  const capById = collectChannelCapById(tree);
  return {
    channels: offeredIds.map((id) => ({ id, maxQuantity: capById.get(id) ?? null })),
    isForAllChannels: false,
    channelDefaultMaxQuantity: null,
  };
};

export const toCheckInTime = (
  entranceTimeSlot: AdmissionFormValue['advanced']['entranceTimeSlot'],
  offer: Admission | Pass | null | undefined,
  timezone: string
): CreateAdmissionRequest['checkInTime'] =>
  entranceTimeSlot.mode === ENTRANCE_TIME_SLOT_MODE_LIMITED
    ? formatValidTimeToBackend({
        formValidFrom: entranceTimeSlot.validFrom,
        formValidFromTime: entranceTimeSlot.validFromTime,
        formValidUntil: entranceTimeSlot.validUntil,
        formValidUntilTime: entranceTimeSlot.validUntilTime,
        offer,
        timezone,
      })
    : { validFrom: null, validUntil: null };

export const toAccessCode = (accessCode: AdmissionFormValue['distribution']['accessCode']): string | null =>
  accessCode?.enabled && accessCode.code !== '' ? accessCode.code : null;

export const toBackendTranslations = (
  translations: Translations,
  mainLanguage: SupportedLanguages,
  translationsEnabled: boolean
): Translations => {
  // With per-locale translations on, keep each filled locale and null the blanks. With it off, send only
  // the main language; every other locale resets to null, clearing anything the offer had stored before.
  if (translationsEnabled) return clearEmptyValues(translations) as Translations;
  const cleared = {} as Translations;
  for (const locale of Object.keys(translations)) {
    const main = locale === mainLanguage ? translations[locale]?.trim() ?? '' : '';
    cleared[locale] = main === '' ? null : main;
  }
  return cleared;
};

type ItalyRegulation = NonNullable<NonNullable<CreateAdmissionRequest['settings']>['italyRegulation']>;

export const toItalyRegulation = (
  basicInfo: AdmissionFormValue['basicInfo'],
  regulation: AdmissionFormValue['regulation']
): ItalyRegulation | null => {
  const hasRegulationData =
    basicInfo.ddp !== undefined ||
    regulation.siaeStatus !== undefined ||
    regulation.excludeFromC1 !== undefined ||
    regulation.allows18App !== undefined ||
    regulation.abbonamentoId !== undefined;
  if (!hasRegulationData) return null;

  return {
    dirittoPrevendita: calculateDirittoPrevendita(basicInfo.ddp),
    isSiaeExcluded: regulation.siaeStatus === SIAE_STATUS_EXCLUDED || regulation.excludeFromC1 === true,
    ...(regulation.siaeStatus !== undefined && { isSiaeApproved: regulation.siaeStatus === SIAE_STATUS_APPROVED }),
    ...(regulation.allows18App !== undefined && { allows18App: regulation.allows18App }),
    ...(regulation.abbonamentoId !== undefined && { abbonamentoId: regulation.abbonamentoId }),
  };
};

const toOfferReleasedBefore = (
  startType: string,
  selectedOfferTriggerId: string | null,
  offer: Admission | null | undefined
): string | null => {
  if (startType === SALES_START_TYPE_AFTER_OFFER) return selectedOfferTriggerId ?? offer?.offerReleasedBefore ?? null;
  return null;
};

export const isNominativeDirty = (
  current: { settings?: { isNominative?: boolean } | null },
  baseline: { settings?: { isNominative?: boolean } | null }
): boolean => current.settings?.isNominative !== baseline.settings?.isNominative;

export const diffAdmissionRequest = (
  current: CreateAdmissionRequest,
  baseline: CreateAdmissionRequest,
  alwaysKeep: (keyof CreateAdmissionRequest)[] = []
): CreateAdmissionRequest => {
  const nominativeChanged = isNominativeDirty(current, baseline);
  const changed: CreateAdmissionRequest = {};
  for (const key of Object.keys(current) as (keyof CreateAdmissionRequest)[]) {
    const coupledToNominative = nominativeChanged && key === 'ticketHolderFormFields';
    if (alwaysKeep.includes(key) || coupledToNominative || !valuesEqual(current[key], baseline[key])) {
      Object.assign(changed, { [key]: current[key] });
    }
  }
  return changed;
};

export const applySalesTimeGranularity = (
  payload: CreateAdmissionRequest,
  baseline: CreateAdmissionRequest,
  startType: string | undefined
): void => {
  if (!payload.salesTime) return;
  const openingChanged = !valuesEqual(payload.salesTime.openingTime, baseline.salesTime?.openingTime);
  const closingChanged = !valuesEqual(payload.salesTime.closingTime, baseline.salesTime?.closingTime);

  const granular: NonNullable<CreateAdmissionRequest['salesTime']> = {};
  if (openingChanged && payload.salesTime.openingTime !== undefined)
    granular.openingTime = payload.salesTime.openingTime;
  if (closingChanged && payload.salesTime.closingTime !== undefined)
    granular.closingTime = payload.salesTime.closingTime;

  if (Object.keys(granular).length === 0) delete payload.salesTime;
  else payload.salesTime = granular;

  if (openingChanged && startType !== SALES_START_TYPE_AFTER_OFFER) payload.offerReleasedBefore = null;
};

const CHANNEL_COUPLED_SETTINGS = new Set<string>(['isForAllChannels', 'channelDefaultMaxQuantity']);

export const isChannelGroupDirty = (current: CreateAdmissionRequest, baseline: CreateAdmissionRequest): boolean =>
  !valuesEqual(current.channels, baseline.channels) ||
  !valuesEqual(current.settings?.isForAllChannels, baseline.settings?.isForAllChannels) ||
  !valuesEqual(current.settings?.channelDefaultMaxQuantity, baseline.settings?.channelDefaultMaxQuantity);

export const applySettingsGranularity = <S extends object>(
  payload: { settings?: S },
  currentSettings: S | null | undefined,
  baselineSettings: S | null | undefined,
  isStatusDirty: boolean,
  isChannelsDirty: boolean
): void => {
  if (!currentSettings) return;
  const granular: Partial<S> = {};
  for (const key of Object.keys(currentSettings) as (keyof S)[]) {
    const name = key as string;
    const changed = !valuesEqual(currentSettings[key], baselineSettings?.[key]);
    const coupledToStatus = name === 'isHidden' && isStatusDirty;
    const coupledToChannels = isChannelsDirty && CHANNEL_COUPLED_SETTINGS.has(name);
    if (changed || coupledToStatus || coupledToChannels) granular[key] = currentSettings[key];
  }
  if (Object.keys(granular).length > 0) payload.settings = granular as S;
  else delete payload.settings;
};

export const diffTeamCaps = (
  current: DistributionChannelLimitValue | undefined,
  baseline: DistributionChannelLimitValue | undefined
): { teamId: string; maxQuantity: number | null }[] => {
  const baselineMax = new Map((baseline?.channels.teams ?? []).map((team) => [team.id, team.maxTicketQuantity]));
  const changed: { teamId: string; maxQuantity: number | null }[] = [];
  for (const team of current?.channels.teams ?? []) {
    if (team.maxTicketQuantity !== (baselineMax.get(team.id) ?? null)) {
      changed.push({ teamId: team.id, maxQuantity: team.maxTicketQuantity });
    }
  }
  return changed;
};

export const resolveTicketsLeftThreshold = (
  lowStock: AdmissionFormValue['advanced']['lowStockThreshold'],
  fallback: number,
  isCreate: boolean
): number | null => {
  if (!lowStock) return null;
  if (!isCreate && !lowStock.enabled) return null;
  return lowStock.quantity === '' ? fallback : Number(lowStock.quantity);
};

const toBottleServiceFields = (basicInfo: AdmissionFormValue['basicInfo']): Partial<CreateAdmissionRequest> => {
  const extra = basicInfo.extraGuests;
  const extraGuestsOn = extra?.enabled ?? false;
  return {
    ...(basicInfo.guestsPerAdmission && { guestCapacity: Number(basicInfo.guestsPerAdmission) }),
    ...(extra !== undefined && {
      extraGuestMaxQuantity: extraGuestsOn && extra.maxQty ? Number(extra.maxQty) : null,
      pricePerExtraGuest: extraGuestsOn && extra.pricePerGuest ? Number(extra.pricePerGuest) : null,
    }),
  };
};

export const toCreateAdmissionRequest = ({
  value,
  type,
  currency,
  mainLanguage,
  eventId,
  eventStartingTime,
  eventEndingTime,
  timezone,
  offer,
  offerPresetId,
  defaultTicketsLeftThreshold,
  formFieldPresets,
  selectedOfferTriggerId,
}: ToBackendContext): CreateAdmissionRequest => {
  const { basicInfo, advanced, appearance, vatRate, regulation, addOns, formFields } = value;
  const isCreate = !offer;

  const lowStock = advanced.lowStockThreshold;
  const ticketsLeftThreshold = resolveTicketsLeftThreshold(lowStock, defaultTicketsLeftThreshold, isCreate);

  const drinks = advanced.drinks;
  const drinksReset = isCreate ? undefined : 0;
  const drinksIncluded = drinks?.enabled && drinks.quantity !== '' ? Number(drinks.quantity) : drinksReset;

  const { channels, isForAllChannels, channelDefaultMaxQuantity } = toChannelSettings(value.distribution.channelLimit);

  const request: CreateAdmissionRequest = {
    ...(isCreate && { eventId, onlinePaymentPercentage: 100 }),
    ...(offerPresetId && { offerPresetId }),
    salesStatus: formatSalesStatusToBackend({ offer, status: value.status, prevStatus: offer?.status }),
    settings: {
      isHidden: value.status === ADMISSION_STATUS_HIDDEN,
      distribution: value.distribution.mode,
      isForAllChannels,
      channelDefaultMaxQuantity,
      isNominative: formFields.isNominative,
    },
    channels,
    price: formatPriceToBackend({
      price: basicInfo.price,
      downPayment: basicInfo.downpayment,
      currency,
      type,
    }),
    quantity: basicInfo.quantity ? Number(basicInfo.quantity) : null,
    itemsPerTransaction: formatItemsPerTransactionToBackend({
      min: basicInfo.itemsPerTransaction.min,
      max: basicInfo.itemsPerTransaction.max,
      quantity: Number(basicInfo.quantity),
      type,
    }),
    name: toBackendTranslations(basicInfo.title, mainLanguage, basicInfo.translationsEnabled),
    description: toBackendTranslations(basicInfo.description, mainLanguage, basicInfo.translationsEnabled),
    color: appearance.color ?? null,
    hasMandatoryAddOns: addOns.hasMandatoryAddOns,
    addOnChoiceMode: addOns.addOnChoiceMode,
    addOns: addOns.selectedAddOnIds,
    ...(drinksIncluded !== undefined && { drinksIncluded }),
    ...(lowStock && { ticketsLeftThreshold }),
    vatRate: vatRate.vatRate,
  };

  if (basicInfo.salesWindow) {
    const { startDate, startTime, startType, endDate, endTime, endType } = basicInfo.salesWindow;
    request.salesTime = formatSalesTimeToBackend({
      offer,
      salesStartDate: startDate,
      salesStartTime: startTime,
      salesEndDate: endDate,
      salesEndTime: endTime,
      salesStartType: startType,
      salesEndType: endType,
      eventStartingTime,
      eventEndingTime,
      timezone,
    });
    request.offerReleasedBefore = toOfferReleasedBefore(startType, selectedOfferTriggerId, offer);
  }

  if (type === 'bottleService') {
    Object.assign(request, toBottleServiceFields(basicInfo));
  }

  const italyRegulation = toItalyRegulation(basicInfo, regulation);
  if (italyRegulation) {
    request.settings = { ...request.settings, italyRegulation };
  }

  request.checkInTime = toCheckInTime(advanced.entranceTimeSlot, offer, timezone);
  request.accessCode = toAccessCode(value.distribution.accessCode);

  request.bookingFormFields = mapHolderGroupToBackend(
    formFields.mainHolder,
    offer?.bookingFormFields,
    formFieldPresets
  );
  request.ticketHolderFormFields = formFields.isNominative
    ? mapHolderGroupToBackend(formFields.ticketHolder, offer?.ticketHolderFormFields, formFieldPresets)
    : [];

  return request;
};
